import React, { useState, useRef, useCallback } from "react";
import "./upload.css";
import { uploadDocument } from "../../services/accIqService";
import { useUser } from "../../context/UserContext";

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".txt"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const EXT_TO_DOC_TYPE = {
    ".pdf": "PDF",
    ".doc": "WORD",
    ".docx": "WORD",
    ".txt": "TEXT",
};

export default function UploadModal({ isOpen, onClose, onUploadSuccess }) {
    const { user } = useUser();

    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [dragActive, setDragActive] = useState(false);

    const fileInputRef = useRef(null);

    /* ---------- helpers ---------- */

    const getExtension = (name) =>
        "." + name.split(".").pop().toLowerCase();

    const validateFiles = (incoming) => {
        const newErrors = [];
        const accepted = [];

        Array.from(incoming).forEach((file) => {
            const ext = getExtension(file.name);
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                newErrors.push(`"${file.name}" — Only PDF, Word, and Text files are allowed.`);
            } else if (file.size > MAX_FILE_SIZE) {
                newErrors.push(`"${file.name}" — File size must be less than 5MB.`);
            } else {
                accepted.push(file);
            }
        });

        return { accepted, newErrors };
    };

    const addFiles = (incoming) => {
        setSuccessMsg("");
        const { accepted, newErrors } = validateFiles(incoming);
        setErrors(newErrors);
        if (accepted.length) {
            setFiles((prev) => [...prev, ...accepted]);
        }
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const resetState = () => {
        setFiles([]);
        setErrors([]);
        setSuccessMsg("");
        setUploading(false);
        setDragActive(false);
    };

    /* ---------- drag & drop ---------- */

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            addFiles(e.target.files);
            e.target.value = ""; // reset so same file can be re‑added
        }
    };

    /* ---------- close (with reset) ---------- */

    const handleClose = () => {
        resetState();
        onClose();
    };

    /* ---------- actions ---------- */

    const handleReset = () => {
        resetState();
    };

    const handleSubmit = async () => {
        if (!files.length) {
            setErrors(["Please add at least one file before submitting."]);
            return;
        }

        setUploading(true);
        setErrors([]);
        setSuccessMsg("");

        const userId = user?._id || user?.id || "";
        const client = user?.company_name_for_growth_iq || user?.company_name || "";

        const uploadErrors = [];

        // One API call per file, auto-detect document type
        for (const file of files) {
            const ext = getExtension(file.name);
            const documentType = EXT_TO_DOC_TYPE[ext] || "PDF";

            try {
                await uploadDocument(userId, client, "global", documentType, [file]);
            } catch (err) {
                const msg =
                    err?.response?.data?.message ||
                    `Failed to upload "${file.name}". Please try again.`;
                uploadErrors.push(msg);
            }
        }

        setUploading(false);

        if (uploadErrors.length) {
            setErrors(uploadErrors);
        } else {
            setSuccessMsg("All documents uploaded successfully!");
            setFiles([]);
            if (onUploadSuccess) onUploadSuccess();
        }
    };

    /* ---------- format size ---------- */

    const formatSize = (bytes) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    };

    /* ---------- render ---------- */

    if (!isOpen) return null;

    return (
        <div className="upload-overlay" onClick={handleClose}>
            <div className="upload-modal" onClick={(e) => e.stopPropagation()}>

                {/* HEADER */}
                <div className="upload-modal-header">
                    <h3>Upload Document</h3>
                    <div className="upload-modal-close" onClick={handleClose}>✕</div>
                </div>

                {/* SCROLLABLE BODY */}
                <div className="upload-modal-body">

                    {/* DROP ZONE */}
                    <div
                        className={`upload-dropzone ${dragActive ? "upload-dropzone-active" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <i className="fa fa-cloud-upload upload-dropzone-icon"></i>
                        <p className="upload-dropzone-text">
                            Drag &amp; drop files here, or <span>browse</span>
                        </p>
                        <p className="upload-dropzone-hint">
                            PDF, DOC, DOCX, TXT — max 5 MB each
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={handleFileInput}
                            hidden
                        />
                    </div>

                    {/* ERRORS */}
                    {errors.length > 0 && (
                        <div className="upload-error-list">
                            {errors.map((err, i) => (
                                <p key={i} className="upload-error">{err}</p>
                            ))}
                        </div>
                    )}

                    {/* SUCCESS */}
                    {successMsg && (
                        <p className="upload-success">{successMsg}</p>
                    )}

                    {/* FILE LIST */}
                    {files.length > 0 && (
                        <div className="upload-file-list">
                            {files.map((file, i) => (
                                <div key={i} className="upload-file-item">
                                    <div className="upload-file-info">
                                        <i className="fa fa-file-o"></i>
                                        <span className="upload-file-name">{file.name}</span>
                                        <span className="upload-file-size">{formatSize(file.size)}</span>
                                    </div>
                                    <button
                                        className="upload-file-remove"
                                        onClick={() => removeFile(i)}
                                        title="Remove"
                                    >✕</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* FOOTER — always visible */}
                <div className="upload-modal-footer">
                    <button
                        className="upload-reset-btn"
                        onClick={handleReset}
                        disabled={uploading}
                    >
                        Reset
                    </button>
                    <button
                        className="upload-submit-btn"
                        onClick={handleSubmit}
                        disabled={uploading || files.length === 0}
                    >
                        {uploading ? "Uploading…" : "Submit"}
                    </button>
                </div>

            </div>
        </div>
    );
}
